import { Copy, Check, CircleCheck, CircleX } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table'
import { Tooltip } from '@/components/ui/Tooltip'
import type EmailDomainConfiguration from '@/types/serializers/EmailDomainConfiguration'

type DnsRecord = EmailDomainConfiguration['dns_records'][number]

interface DnsRecordsTableProps {
  records: DnsRecord[]
  label: string
  checked: boolean
}

export function DnsRecordsTable({ records, label, checked }: DnsRecordsTableProps) {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-2">{label}</p>
      <div className="rounded-md border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {checked && <TableHead className="w-10">Status</TableHead>}
              <TableHead className="w-20">Type</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Value</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.map((record, i) => (
              <DnsRecordRow key={i} record={record} checked={checked} />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function DnsRecordRow({ record, checked }: { record: DnsRecord; checked: boolean }) {
  const [copied, setCopied] = useState<'name' | 'value' | null>(null)

  const copy = (text: string, field: 'name' | 'value') => {
    navigator.clipboard.writeText(text)
    setCopied(field)
    toast.success('Copied to clipboard')
    setTimeout(() => setCopied(null), 2000)
  }

  return (
    <TableRow>
      {checked && (
        <TableCell>
          <StatusIcon record={record} />
        </TableCell>
      )}
      <TableCell className="font-mono text-xs">{record.type}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <code className="text-xs break-all">{record.name}</code>
          <button onClick={() => copy(record.name, 'name')} className="shrink-0 cursor-pointer p-1 rounded hover:bg-muted">
            {copied === 'name' ? <Check className="size-3 text-emerald-500" /> : <Copy className="size-3 text-muted-foreground" />}
          </button>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <code className="text-xs break-all">{record.value}</code>
          <button onClick={() => copy(record.value, 'value')} className="shrink-0 cursor-pointer p-1 rounded hover:bg-muted">
            {copied === 'value' ? <Check className="size-3 text-emerald-500" /> : <Copy className="size-3 text-muted-foreground" />}
          </button>
        </div>
      </TableCell>
    </TableRow>
  )
}

function StatusIcon({ record }: { record: DnsRecord }) {
  if (record.resolved === undefined) return null

  if (record.resolved) {
    return (
      <Tooltip content="Record found">
        <CircleCheck className="size-4 text-emerald-500" />
      </Tooltip>
    )
  }

  return (
    <Tooltip content={
      record.actual_value
        ? <>Found: <code className="text-xs">{record.actual_value}</code></>
        : 'Record not found'
    }>
      <CircleX className="size-4 text-destructive" />
    </Tooltip>
  )
}
